import styled from '@emotion/styled'
import { Modal, Button, notification } from 'antd'
import BigNumber from 'bignumber.js'
import { useEffect, useState, useMemo } from 'react'

import error_icon from '@/assets/error_icon.png'
import { useLocalStorage } from '@/context/localstorage-context'
import type { ImgKeysType, ImgType } from '@/types/xwan-farming-type'
import { isNumber } from '@/utils'

const XWanStakeModal = ({
  onCancel,
  visible,
  balance,
  onDeposit,
  onApprove,
  loading,
  showApprove,
  handleImgName,
}: {
  onCancel: () => void
  visible: boolean
  balance: null | {
    balance: string
    per: string
  }
  onDeposit: (amount: string) => void
  onApprove: () => void
  loading: boolean
  showApprove: boolean
  handleImgName: (name: ImgKeysType) => ImgType
}) => {
  const [inpValue, setInpValue] = useState('')
  const { theme } = useLocalStorage()
  const isdark = useMemo(() => theme === 'dark', [theme])

  const handleInp = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let value = e.target.value
    if (!isNumber(value)) {
      if (value !== '') return
    }
    if (Number(value) < 0) {
      return
    }
    const arr = value.split('')
    if (arr.length > 1 && arr[0] === '0' && arr[1] !== '.') {
      value = String(Number(value))
    }
    setInpValue(value)
  }

  const handleMax = () => {
    setInpValue(balance?.balance ? balance.balance : '0')
  }

  // const handleTest = () => {
  //   let value = inpValue;
  //   if (/[eE+\-]/.test(value)) {
  //     return false
  //   }
  //   const parts = value.split('.');
  //   if (parts.length > 2) {
  //     value = parts[0] + '.' + parts.slice(1).join('');
  //     return false
  //   }
  //   return true
  // }

  const handleDeposit = () => {
    onDeposit(inpValue)
  }

  useEffect(() => {
    setInpValue('0')
  }, [visible])

  return (
    <Modal
      maskClosable={true}
      closable={true}
      destroyOnClose={true}
      width={820}
      mask={true}
      open={visible}
      title={null}
      footer={null}
      onCancel={onCancel}
      centered
      className={`${isdark ? 'darkModal' : 'lightModal'} xwan-stake-modal`}
    >
      <Title className="text-light-0 dark:text-white">
        Stake {balance?.per ? handleImgName(balance.per as ImgKeysType) : ''}
      </Title>
      <InpCon className="border border-solid border-light-111 dark:border-dark-111">
        <Inp
          type="number"
          name="stake"
          className="text-light-111 dark:text-dark-111"
          value={inpValue}
          onChange={handleInp}
        />
        <MaxCon>
          <span className="dark:text-dark-0">
            {balance?.per ? handleImgName(balance.per as ImgKeysType) : ''}
          </span>
          <div className="w-px h-6 bg-light-444 dark:bg-dark-1777 mx-3"></div>
          <MaxBtn
            className="text-light-111 dark:text-dark-111"
            onClick={handleMax}
          >
            Max
          </MaxBtn>
        </MaxCon>
      </InpCon>
      <div className="flex justify-between items-center pt-3 pb-4">
        <BalanceTxt className="text-light-0 dark:text-dark-0">
          Balance
        </BalanceTxt>
        <BalanceValueTxt className="text-light-111 dark:text-dark-0 flex items-center">
          <span className="text-light-111 dark:text-dark-111">
            {balance?.balance ? balance.balance : 'N/A'}
          </span>
          &nbsp;{balance?.per ? handleImgName(balance.per as ImgKeysType) : ''}
        </BalanceValueTxt>
      </div>
      <div className="flex justify-between items-center mb-6">
        <CancelBtn
          className="bg-light-1101 dark:bg-dark-1777 text-light-444 dark:text-dark-0"
          onClick={onCancel}
        >
          Cancel
        </CancelBtn>
        <StakeBtn
          className="bg-light-111 dark:bg-dark-111 text-white"
          loading={loading}
          type="primary"
          onClick={() => {
            if (loading) return
            if (showApprove) {
              onApprove()
              return
            }
            const value = new BigNumber(+inpValue)
            if (
              !balance?.balance ||
              value.eq(0) ||
              value.gt(Number(balance?.balance))
            ) {
              notification.error({
                message: 'Error',
                description: 'Invalid Amount.',
                className: `${
                  isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'
                }`,
                icon: <img width={30} src={error_icon} alt="notification" />,
              })
              return
            }
            handleDeposit()
          }}
        >
          {loading ? 'Loading' : showApprove ? 'Approve' : 'Stake'}
        </StakeBtn>
      </div>
    </Modal>
  )
}

export default XWanStakeModal

const Title = styled.div`
  font-size: 24px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  margin-bottom: 2.4rem;
`

const InpCon = styled.div`
  padding: 1.6rem 2.4rem;
  border-radius: 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Inp = styled.input`
  width: 0;
  flex: 1;
  outline: none;
  font-size: 32px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  background: none;
  border: none;

  &:focus {
    box-shadow: none;
  }
`

const MaxCon = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.8rem;
`

const MaxBtn = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
`

const BalanceTxt = styled.p`
  font-family: Inter;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  margin: 0;
`

const BalanceValueTxt = styled.p`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: right;
  margin: 0;
`

const CancelBtn = styled(Button)`
  flex: 1;
  margin-right: 2.4rem;
  padding: 2.4rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 16px;
  font-family: Inter;
  font-size: 18px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  height: fit-content;
  border: none;
`

const StakeBtn = styled(CancelBtn)`
  margin-right: 0;
`
